/** The signed-in user's profile record (GET /me), shared by the header and the profile page. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api, ApiError } from '@/api/client'
import type { User } from '@/lib/types'

interface Value {
  profile: User | null
  error: string
  /** The server rejected the sign-in itself, even after a fresh token: retrying cannot help. */
  unauthorized: boolean
  loading: boolean
  setProfile: (u: User) => void
  reload: () => Promise<void>
}
const Ctx = createContext<Value | null>(null)

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfileValue] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [unauthorized, setUnauthorized] = useState(false)
  const profileRef = useRef(profile)
  profileRef.current = profile
  const generation = useRef(0)
  /** `quiet` keeps whatever is on screen: a background refresh must not swap the error page out under a click. */
  const load = useCallback(async (quiet: boolean) => {
    const current = ++generation.current
    if (!profileRef.current && !quiet) setLoading(true)
    try {
      const value = await api.get<User>('/me')
      if (current === generation.current) { setProfileValue(value); setError(''); setUnauthorized(false) }
    } catch (e) {
      if (current === generation.current) {
        setProfileValue(null)
        setError(e instanceof Error ? e.message : 'Could not load your profile.')
        setUnauthorized(e instanceof ApiError && e.status === 401)
      }
    } finally {
      if (current === generation.current) setLoading(false)
    }
  }, [])
  const reload = useCallback(() => load(false), [load])
  const setProfile = useCallback((value: User) => {
    // A successful save takes precedence over an older in-flight GET /me.
    generation.current++
    profileRef.current = value
    setProfileValue(value)
    setError('')
    setUnauthorized(false)
    setLoading(false)
  }, [])
  useEffect(() => {
    const refresh = () => void load(true)
    void load(false)
    window.addEventListener('focus', refresh)
    window.addEventListener('review-access-check', refresh)
    return () => {
      generation.current++
      window.removeEventListener('focus', refresh)
      window.removeEventListener('review-access-check', refresh)
    }
  }, [load])
  return <Ctx.Provider value={{ profile, error, unauthorized, loading, setProfile, reload }}>{children}</Ctx.Provider>
}

export function useProfile(): Value {
  const v = useContext(Ctx)
  if (!v) throw new Error('useProfile must be used inside ProfileProvider')
  return v
}

/** Profile fields collected at sign-up, parked until the first signed-in request can save them. */
export const PENDING_PROFILE_KEY = 'seldon.pendingProfile'
